import axios from '@/plugins/axios';

const sortByDate = (a, b) => {
  const dateA = new Date(a?.submission?.submittedAt);
  const dateB = new Date(b?.submission?.submittedAt);
  return dateA - dateB;
};

const state = {
  submissionType: null,
  saved: false,
  currentSubmission: {
    id: null,
    lender: null,
    product: null,
    states: [],
    formNumber: '',
    effectiveOnApproval: false,
    effectiveAt: null,
    documents: [],
    hasUnsavedChanges: false,
  },
  submissionStepper: {
    currentStep: 1,
    completedSteps: 0,
  },
  previousSubmissionNumber: null,
  previousSubmissionId: null,
  previousSubmission: null,
  lawChangeSubmissions: [],
  relatedSubmissions: [],
};


const mutations = {
  setSubmissionType: (currentState, payload) => {
    currentState.submissionType = payload;
  },
  setSubmission: (currentState, payload) => {
    currentState.currentSubmission = {
      ...currentState.currentSubmission, ...payload,
    };
    currentState.saved = false;
  },
  saved: (currentState) => {
    currentState.saved = true;
    currentState.currentSubmission.hasUnsavedChanges = false;
  },
  setSubmissionStepper: (currentState, payload) => {
    // only scroll to top if step is changing
    if (payload.currentStep && payload.currentStep !== currentState.submissionStepper.currentStep) {
      window.scrollTo(0, 0);
    }
    currentState.submissionStepper = { ...currentState.submissionStepper, ...payload };
  },
  setPreviousSubmissionNumber: (currentState, payload) => {
    currentState.previousSubmissionNumber = payload;
  },
  setPreviousSubmission: (currentState, payload) => {
    currentState.previousSubmission = payload;
  },
  setLawChangeSubmissions: (currentState, payload) => {
    currentState.lawChangeSubmissions = payload;
  },
  setRelatedSubmissions: (currentState, payload) => {
    currentState.relatedSubmissions = payload;
  },
  resetSubmission: (currentState) => {
    currentState.currentSubmission = {
      id: null,
      lender: null,
      product: null,
      states: [],
      formNumber: '',
      effectiveOnApproval: false,
      effectiveAt: null,
      documents: [],
    };
  },
  resetSubmissionStepper: (currentState) => {
    window.scrollTo(0, 0);
    currentState.submissionStepper = {
      currentStep: 1,
      completedSteps: 0,
    };
  },
  addNote(currentState, note) {
    if (currentState?.currentSubmission?.notes) {
      currentState.currentSubmission.notes.push(note);
    }
  },
  editNote(currentState, note) {
    if (currentState?.currentSubmission?.notes) {
      const { currentSubmission: { notes } } = currentState;
      const idx = notes.findIndex((n) => n.id === note?.id);
      if (idx !== -1) {
        notes.splice(idx, 1, note);
      }
    }
  },
  deleteNote(currentState, noteId) {
    if (currentState?.currentSubmission?.notes) {
      const { currentSubmission: { notes } } = currentState;
      const idx = notes.findIndex((n) => n.id === noteId);
      if (idx !== -1) {
        notes.splice(idx, 1);
      }
    }
  },
  editDocument(currentState, document) {
    if (currentState?.currentSubmission?.documents) {
      const { currentSubmission: { documents } } = currentState;
      const idx = documents.findIndex((d) => d.id === document?.id);
      if (idx !== -1) {
        documents.splice(idx, 1, document);
      }
    }
  },
  deleteDocumentFile(currentState, documentId) {
    if (currentState?.currentSubmission?.documents) {
      const { currentSubmission: { documents } } = currentState;
      const document = documents.find((d) => d.id === documentId);
      if (document) {
        document.uploadedFileName = null;
        document.legacyFile = null;
      }
    }
  },
  addFlag(currentState, flag) {
    if (currentState?.currentSubmission?.flags) {
      currentState.currentSubmission.flags.push(flag);
    }
  },
  deleteFlag(currentState, flagId) {
    if (currentState?.currentSubmission?.flags) {
      const { currentSubmission: { flags } } = currentState;
      const idx = flags.findIndex((n) => n.id === flagId);
      if (idx !== -1) {
        flags.splice(idx, 1);
      }
    }
  },
};

const actions = ({
  initSubmission({ commit, state: currentState }, { submissionType }) {
    return new Promise((resolve) => {
      const params = {
        'submission-type': submissionType,
      };
      const originalSubmission = currentState.previousSubmission?.id
        ?? currentState.previousSubmissionId;
      const isInitial = submissionType === 'initial';

      if (originalSubmission && !isInitial) {
        params['original-submission'] = originalSubmission;
      }

      axios.post('/submissions', {}, { params })
        .then((response) => {
          const dbSubmission = response?.data;
          commit('setSubmission', { id: dbSubmission?.id, submissionNumber: dbSubmission.submissionNumber });
          commit('saved');
          resolve();
        });
    });
  },
  updateSubmission({ state: currentState, dispatch }) {
    return new Promise((resolve, reject) => {
      const url = `/submissions/${currentState.currentSubmission.id}`;
      axios.put(url, currentState.currentSubmission)
        .then(() => {
          dispatch('getSubmission', currentState.currentSubmission.id)
            .then(resolve).catch(reject);
        })
        .catch(reject);
    });
  },
  getSubmission({ commit }, id) {
    return new Promise((resolve, reject) => {
      const url = `/submissions/${id}`;
      axios.get(url).then((response) => {
        commit('setSubmission', response?.data);
        commit('saved');
        resolve();
      }).catch(reject);
    });
  },
  getRelatedSubmissions({ commit, state: currentState }) {
    const { id, relatedSubmissionId, submissionType } = currentState.currentSubmission;
    const payload = [];
    const childAndSiblingParams = { 'filters[relatedSubmissionId]': relatedSubmissionId ? `${id},${relatedSubmissionId}` : id };
    const promises = [
      axios.get('/submissions', { params: childAndSiblingParams })
        .then((response) => {
          if (response.data?.items) {
            const childSubmissions = response.data.items
              .filter((s) => s.relatedSubmissionId === id);
            childSubmissions.forEach((s) => payload.push({ relation: 'C', submission: s }));
            const siblingSubmissions = response.data.items
              .filter((s) => s.relatedSubmissionId === relatedSubmissionId && s.id !== id);
            siblingSubmissions.forEach((s) => payload.push({ relation: 'B', submission: s }));
          }
        }),
    ];

    if (relatedSubmissionId) {
      const parentParams = { 'filters[id]': relatedSubmissionId };
      promises.push(axios.get('/submissions', { params: parentParams })
        .then((response) => {
          response.data.items.forEach((s) => payload.push({ relation: 'A', submission: s }));
        }));
    }

    Promise.all(promises).finally(() => {
      payload.sort(sortByDate);
      commit('setRelatedSubmissions', payload);
      const previousSubmission = payload.find((s) => s.relation === 'A');
      if (previousSubmission) {
        commit('setPreviousSubmission', previousSubmission.submission);
      }
      commit('setSubmissionType', submissionType.toLowerCase());
    });
  },
  getPreviousSubmissionByNumber({ dispatch }, payload) {
    const params = {
      'filters[submissionNumber]': payload.submissionNumber,
      'filters[lender]': payload.lender,
    };

    return new Promise((resolve, reject) => {
      axios.get('/submissions', { params })
        .then((response) => {
          if (!response.data.items || response.data.items.length === 0) {
            reject(new Error('not-found'));
          } else {
            const previousSubmissionId = response.data.items[0].id;
            resolve(dispatch('getPreviousSubmissionById', { submissionId: previousSubmissionId }));
          }
        })
        .catch(reject);
    });
  },
  getPreviousSubmissionById({ commit, state: { submissionType, currentSubmission } },
    { submissionId }) {
    return new Promise((resolve, reject) => {
      axios.get(`/submissions/${submissionId}`)
        .then((res) => {
          const previous = res.data;
          const submissionPayload = { product: previous.product, lender: previous.lender };

          if (submissionType === 'resubmission') {
            if (previous.submissionType === 'Initial'
              && previous.states.some((s) => s.status === 'Rejected'
              && previous.isResubmittable)) {
              submissionPayload.states = previous.states.filter((s) => s.status === 'Rejected');
            }
          } else if (submissionType === 'sos' || submissionType === 'mbr') {
            submissionPayload.states = previous.states
              .filter((s) => s.status === 'Approved' && new Date(s.expiresAt) > new Date());
          }

          if (previous.status !== 'Reviewed') {
            reject(new Error('invalid-status'));
            return;
          }
          if (['resubmission', 'sos', 'mbr'].includes(submissionType)
            && !submissionPayload?.states?.length) {
            reject(new Error('no-states'));
            return;
          }

          // only set current submission here if we are starting a new submission,
          // not if we are resuming an existing draft (which has an id already)
          if (!currentSubmission?.id) {
            commit('setSubmission', submissionPayload);
          }
          commit('setPreviousSubmission', previous);
          commit('saved');
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  },
  getLawChangeEligibleSubmissions({ commit }, { state: lookupState, lender }) {
    const params = { 'filters[states]': lookupState, 'filters[status]': 'Approved', 'filters[lender]': lender };
    return new Promise((resolve, reject) => {
      axios.get('/submissions', { params })
        .then((res) => {
          // filtering here to only bring back submissions in which the lookupState is approved
          // and the submission itself is reviewed (and submission is for current lender)
          const items = res.data.items.filter((s) => s.states.find((st) => st.state === lookupState && st.status === 'Approved'));
          commit('setLawChangeSubmissions', items);
          resolve();
        })
        .catch(() => {
          reject();
        });
    });
  },
  deleteSubmission(_, { id }) {
    return new Promise((resolve, reject) => {
      if (id) {
        axios.delete(`/submissions/${id}`)
          .then(() => {
            resolve();
          })
          .catch(() => {
            reject();
          });
      } else {
        resolve();
      }
    });
  },
  createNote({ state: currentState, commit }, note) {
    return new Promise((resolve, reject) => {
      const url = `/submissions/${currentState.currentSubmission.id}/notes/`;

      axios.post(url, { ...note })
        .then((resp) => {
          commit('addNote', resp.data);
          resolve();
        })
        .catch(reject);
    });
  },
  updateNote({ state: currentState, commit }, note) {
    return new Promise((resolve, reject) => {
      const url = `/submissions/${currentState.currentSubmission.id}/notes/${note.id}`;

      axios.put(url, note)
        .then((resp) => {
          commit('editNote', resp.data);
          resolve();
        })
        .catch(reject);
    });
  },
  deleteNote({ state: currentState, commit }, noteId) {
    return new Promise((resolve, reject) => {
      const url = `/submissions/${currentState.currentSubmission.id}/notes/${noteId}`;

      axios.delete(url)
        .then(() => {
          commit('deleteNote', noteId);
          resolve();
        })
        .catch(reject);
    });
  },
  updateDocumentFile({ state: currentState, commit }, payload) {
    return new Promise((resolve, reject) => {
      const { document, file } = payload;
      const formData = new FormData();
      formData.append('file', file, file.name);
      const url = `/submissions/${currentState.currentSubmission.id}/documents/${document.id}/file`;
      axios.put(url, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then(() => {
          commit('editDocument', { ...document, uploadedFileName: file.name, legacyFile: null });
          resolve();
        })
        .catch(reject);
    });
  },
  deleteDocumentFile({ state: currentState, commit }, documentId) {
    return new Promise((resolve, reject) => {
      const url = `/submissions/${currentState.currentSubmission.id}/documents/${documentId}/file`;

      axios.delete(url)
        .then(() => {
          commit('deleteDocumentFile', documentId);
          resolve();
        })
        .catch(reject);
    });
  },
  createFlag({ state: currentState, commit }, flag) {
    return new Promise((resolve, reject) => {
      const url = `submissions/${currentState.currentSubmission.id}/flags`;
      axios.post(url, flag)
        .then((resp) => {
          commit('addFlag', resp.data);
          resolve();
        })
        .catch(reject);
    });
  },
  deleteFlag({ state: currentState, commit }, flagId) {
    return new Promise((resolve, reject) => {
      const url = `submissions/${currentState.currentSubmission.id}/flags/${flagId}`;
      axios.delete(url)
        .then(() => {
          commit('deleteFlag', flagId);
          resolve();
        })
        .catch(reject);
    });
  },
});

const getters = {
  newSubmissionDetails(currentState) {
    // The API represents the State/Admin relationship differently than the UI, so we need to
    // massage the data some.
    let admins = [{ name: null, states: [] }];
    if (currentState.currentSubmission.states?.length > 0) {
      admins = currentState.currentSubmission.states.map((s) => ({
        name: s.administratorName,
        states: currentState.currentSubmission.states
          // This filter/map gets the states for the current admin.
          .filter((st) => st.administratorName === s.administratorName)
          .map((st) => st.state),
      }))
        // This filter will drop duplicate entries.
        .filter((admin, i, self) => self.findIndex((a) => a.name === admin.name) === i);
    }

    return {
      lender: currentState.currentSubmission.lender,
      product: currentState.currentSubmission.product,
      states: currentState.currentSubmission.states?.map((s) => s.state) || [],
      formNumber: currentState.currentSubmission.formNumber,
      administrators: admins,
      documents: currentState.currentSubmission.documents,
      effectiveOnApproval: currentState.currentSubmission.effectiveOnApproval,
    };
  },
  stateFilters(currentState) {
    const filters = [];
    if (currentState.currentSubmission.product?.states?.length) {
      const productStates = currentState.currentSubmission.product.states;
      filters.push((s) => productStates.includes(s.abbr));
    }

    const { submissionType, previousSubmission } = currentState;
    if (submissionType === 'resubmission' && previousSubmission) {
      const rejectedStates = previousSubmission.states
        .filter((s) => s.status === 'Rejected')
        .map((s) => s.state);

      filters.push((s) => rejectedStates.includes(s.abbr));
    } else if (['sos', 'mbr'].includes(submissionType) && previousSubmission) {
      const approvedStates = previousSubmission.states
        .filter((s) => s.status === 'Approved' && new Date(s.expiresAt) > new Date())
        .map((s) => s.state);

      filters.push((s) => approvedStates.includes(s.abbr));
    }
    return filters;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
